import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  useLocation,
} from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout/Layout'
import Dashboard from './pages/Dashboard/Dashboard'
import Applications from './pages/Applications/Applications'
import ApplicationDetail from './pages/Applications/ApplicationDetail'
import Devices from './pages/Devices/Devices'
import DeviceWebUi from './pages/Devices/DeviceWebUi'
import DeviceMqttConsole from './pages/Devices/DeviceMqttConsole'
import Gateways from './pages/Gateways/Gateways'
import Routers from './pages/Routers/Routers'
import RouterManagement from './pages/Routers/RouterManagement'
import RouterTerminal from './pages/Routers/RouterTerminal'
import Hosts from './pages/Hosts/Hosts'
import HostManagement from './pages/Hosts/HostManagement'
import HostRemoteDisplay from './pages/Hosts/HostRemoteDisplay'
import ManagedDevices from './pages/ManagedDevices/ManagedDevices'
import ManagedDeviceDetail from './pages/ManagedDevices/ManagedDeviceDetail'
import Users from './pages/Users/Users'
import Vpn from './pages/Vpn/Vpn'
import Domains from './pages/Domains/Domains'
import DomainDashboard from './pages/Domains/DomainDashboard'
import DomainSettings from './pages/Domains/DomainSettings'
import DomainWizard from './pages/Domains/DomainWizard'
import ManagedGroups from './pages/Domains/ManagedGroups'
import AuditPage from './pages/Audit/AuditPage'
import DeviceLogsPage from './pages/DeviceLogs/DeviceLogsPage'
import BackupsPage from './pages/Backups/BackupsPage'
import SecurityPage from './pages/Security/SecurityPage'
import TpmDevices from './pages/Tpm/TpmDevices'
import TpmPending from './pages/Tpm/TpmPending'
import TpmDeviceDetail from './pages/Tpm/TpmDeviceDetail'
import Settings from './pages/Settings/Settings'
import Login from './pages/Auth/Login'
import Register from './pages/Auth/Register'
import ForgotPassword from './pages/Auth/ForgotPassword'
import DefinirSenha from './pages/Auth/DefinirSenha'
import DeviceAuth from './pages/Auth/DeviceAuth'
import PrivacyPolicy from './pages/Legal/PrivacyPolicy'
import TermsOfService from './pages/Legal/TermsOfService'
import DataDeletion from './pages/Legal/DataDeletion'
import GestaoIA from './pages/GestaoIA/GestaoIA'

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-gray-600">Carregando...</div>
    </div>
  )
}

function GuestOrRedirect() {
  const { isLoading, isAuthenticated, mustChangePassword } = useAuth()
  const { search } = useLocation()
  if (isLoading) return <LoadingScreen />
  if (isAuthenticated) {
    // Se é um fluxo de autenticação do agente Windows, deixa o Login.jsx tratar
    const params = new URLSearchParams(search)
    if (params.get('source') === 'windows' && params.get('port')) {
      return <Outlet />
    }
    if (mustChangePassword) return <Navigate to="/definir-senha" replace />
    return <Navigate to="/" replace />
  }
  return <Outlet />
}

function RequirePasswordChange() {
  const { isLoading, isAuthenticated, mustChangePassword } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (!mustChangePassword) return <Navigate to="/" replace />
  return <Outlet />
}

function RequireFullSession() {
  const { isLoading, isAuthenticated, mustChangePassword } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (mustChangePassword) return <Navigate to="/definir-senha" replace />
  return <Outlet />
}

function RequireAdmin() {
  const { user } = useAuth()
  const role = (user?.role || user?.Role || '').toLowerCase()
  const isGlobal = Boolean(user?.isGlobalUser || user?.IsGlobalUser)
  if (isGlobal || role === 'admin' || role === 'owner' || role === 'global') {
    return <Outlet />
  }
  return <Navigate to="/" replace />
}

// Gestão IA é só para usuário global — admin/owner de tenant NÃO entra. O gate visual do
// Sidebar não basta: a rota precisa recusar acesso direto pela URL.
function RequireGlobal() {
  const { user } = useAuth()
  const isGlobal = Boolean(user?.isGlobalUser || user?.IsGlobalUser)
  return isGlobal ? <Outlet /> : <Navigate to="/" replace />
}

function CatchAllRedirect() {
  const { isAuthenticated, mustChangePassword } = useAuth()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (mustChangePassword) return <Navigate to="/definir-senha" replace />
  return <Navigate to="/" replace />
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: <GuestOrRedirect />,
    children: [{ index: true, element: <Login /> }],
  },
  {
    path: '/forgot-password',
    element: <GuestOrRedirect />,
    children: [{ index: true, element: <ForgotPassword /> }],
  },
  {
    path: '/register',
    element: <GuestOrRedirect />,
    children: [{ index: true, element: <Register /> }],
  },
  {
    path: '/definir-senha',
    element: <RequirePasswordChange />,
    children: [{ index: true, element: <DefinirSenha /> }],
  },
  {
    path: '/auth/device',
    element: <DeviceAuth />,
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms-of-service',
    element: <TermsOfService />,
  },
  {
    path: '/data-deletion',
    element: <DataDeletion />,
  },
  {
    path: '/',
    element: <RequireFullSession />,
    children: [
      {
        path: 'remote-display/:hostId',
        element: <HostRemoteDisplay />,
      },
      {
        path: 'host-console/:hostId',
        element: <HostManagement />,
      },
      {
        path: 'router-terminal/:routerId',
        element: <RouterTerminal />,
      },
      {
        path: 'router-management/:routerId',
        element: (
          <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <RouterManagement />
          </div>
        ),
      },
      {
        path: 'device-console/:deviceId',
        element: <DeviceMqttConsole />,
      },
      {
        path: 'devices/:deviceId/web-ui/*',
        element: <DeviceWebUi />,
      },
      {
        element: <Layout />,
        children: [
          { index: true, element: <Dashboard /> },
          { path: 'applications', element: <Applications /> },
          { path: 'applications/:applicationId', element: <ApplicationDetail /> },
          { path: 'devices', element: <Devices /> },
          { path: 'gateways', element: <Gateways /> },

          // New unified managed devices interface
          { path: 'managed-devices', element: <ManagedDevices /> },
          { path: 'managed-devices/:deviceId', element: <ManagedDeviceDetail /> },

          // Legacy routes with backward compatibility redirects
          { path: 'routers', element: <Navigate to="/managed-devices?type=router" replace /> },
          { path: 'routers/:routerId/management', element: <RouterManagement /> },
          { path: 'hosts', element: <Navigate to="/managed-devices?type=hosts" replace /> },
          { path: 'hosts/:hostId/management', element: <Navigate to="/hosts" replace /> },
          { path: 'users', element: <RequireAdmin />, children: [{ index: true, element: <Users /> }] },
          { path: 'redes-virtuais', element: <Vpn /> },
          { path: 'vpn', element: <Vpn /> },
          {
            path: 'domains',
            element: <RequireAdmin />,
            children: [
              { index: true, element: <Domains /> },
              { path: 'new', element: <DomainWizard /> },
              { path: ':configId', element: <DomainDashboard /> },
              { path: ':configId/settings', element: <DomainSettings /> },
              { path: ':configId/managed-groups', element: <ManagedGroups /> },
            ],
          },
          { path: 'settings', element: <Settings /> },
          { path: 'audit', element: <AuditPage /> },
          { path: 'logs', element: <DeviceLogsPage /> },
          { path: 'backups', element: <BackupsPage /> },
          { path: 'security', element: <SecurityPage /> },
          {
            path: 'gestao-ia',
            element: <RequireGlobal />,
            children: [{ index: true, element: <GestaoIA /> }],
          },
          {
            path: 'tpm',
            element: <RequireAdmin />,
            children: [
              { index: true, element: <TpmDevices /> },
              { path: 'pending', element: <TpmPending /> },
              { path: 'devices/:deviceId', element: <TpmDeviceDetail /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: <CatchAllRedirect /> },
])

function App() {
  return <RouterProvider router={router} />
}

export default App
